export interface BrowserSubscription {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** What the browser offers for push, behind one interface so tests can play any browser. */
export interface PushPlatform {
  supported(): boolean;
  isIOS(): boolean;
  isStandalone(): boolean;
  permission(): NotificationPermission;
  requestPermission(): Promise<NotificationPermission>;
  /** Resolves once a service worker is active for this page; the push subscription lives on it. */
  ready(): Promise<void>;
  subscribe(publicKey: string): Promise<BrowserSubscription>;
  current(): Promise<BrowserSubscription | null>;
  timezone(): string;
}

export class BrowserPushPlatform implements PushPlatform {
  supported(): boolean {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  }

  isIOS(): boolean {
    const ua = navigator.userAgent;
    return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  }

  isStandalone(): boolean {
    return window.matchMedia?.("(display-mode: standalone)").matches === true || (navigator as { standalone?: boolean }).standalone === true;
  }

  permission(): NotificationPermission {
    return "Notification" in window ? Notification.permission : "denied";
  }

  requestPermission(): Promise<NotificationPermission> {
    return Notification.requestPermission();
  }

  async ready(): Promise<void> {
    await navigator.serviceWorker.ready;
  }

  async subscribe(publicKey: string): Promise<BrowserSubscription> {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) });
    return toBrowserSubscription(subscription);
  }

  async current(): Promise<BrowserSubscription | null> {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? toBrowserSubscription(subscription) : null;
  }

  timezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  }
}

function toBrowserSubscription(subscription: PushSubscription): BrowserSubscription {
  const json = subscription.toJSON();
  return { endpoint: subscription.endpoint, keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" } };
}

/** VAPID keys travel base64url; the Push API wants raw bytes. */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
